import { buildIsoWeekKey } from 'modules/shared/utils';
import { DEPARTMENTS, MAX_WEEKLY_WORKING_HOURS } from '../../constants';

/**
 * Group employees by department
 * @param {Array} employees
 * @returns {Object}
 */
export function groupEmployeesByTaskType(employees) {
  return employees.reduce((acc, employee) => {
    const department = employee.department;

    if (!department) return acc;

    if (!Object.values(DEPARTMENTS).includes(department)) return acc;

    if (!acc[department]) {
      acc[department] = [];
    }

    acc[department].push(employee);

    return acc;
  }, {});
}

/**
 * @param {Array<{ minutes?: number }>} assignedTasks
 * @returns {{ totalMinutes: number, hasAssignments: boolean }}
 */
export function summarizeAssignedTasks(assignedTasks = []) {
  const totalMinutes = assignedTasks.reduce((sum, task) => sum + (task.minutes ?? 0), 0);

  return {
    totalMinutes,
    hasAssignments: assignedTasks.length > 0,
  };
}

/**
 * Builds availability map from assignments
 * Availability is determined strictly by remaining weekly capacity

* @param {Array<{ year: number, week: number, hours?: number }>} assignments
 * @returns {Record<string, { assignedHours: number, remainingHours: number, isAvailable: boolean }>}
 */
export function buildAvailabilityMap(assignments) {
  const map = {};

  if (!Array.isArray(assignments)) return map;

  for (const item of assignments) {
    if (!item) continue;
    const { year, week, hours } = item;
    if (!year || !week) continue;

    const key = buildIsoWeekKey({ year, week });

    if (!map[key]) {
      map[key] = { assignedHours: 0 };
    }

    map[key].assignedHours += Number(hours) || 0;
  }

  for (const key of Object.keys(map)) {
    const assigned = map[key].assignedHours;
    const remaining = Math.max(0, MAX_WEEKLY_WORKING_HOURS - assigned);

    map[key] = {
      assignedHours: assigned,
      remainingHours: remaining,
      isAvailable: remaining > 0,
    };
  }

  return map;
}
